/* When a station reads the board and the book again without being told to.
 *
 * The console reloads when realtime says a row moved. Nothing else made it
 * reload — and on 2026-10-31 that let the Mac front-desk station work for an
 * hour and twenty minutes on a board it had last read at 16:17 UTC. Its socket
 * dropped and reconnected about once a minute all day; every event in between
 * was lost, and a reconnect is not an event. So it never saw Allison's party
 * re-booked onto Thu and Nancy, never saw the Windows station's squares for
 * them, and put two more up against booked lines that no longer existed.
 *
 * Three things now make a station catch up on its own:
 *
 *   - 'rejoin'   a realtime channel has (re)joined. Whatever happened while it
 *                was down was not delivered and never will be.
 *   - 'visible'  the tab has come back to the front, or the network has come
 *                back ('online'). A hidden tab's timers and socket are the
 *                first thing a browser throttles.
 *   - 'tick'     nothing has been read for QUIET_POLL_MS. A socket can look
 *                joined and deliver nothing; this is the floor under all of it.
 *
 * Any of them is throttled to one catch-up per MIN_GAP_MS, so a socket that
 * flaps every few seconds costs one read, not one per flap — and the first
 * join after a load, which always fires, is absorbed by the load it follows.
 *
 * Pure; imports nothing.
 */

export var MIN_GAP_MS = 15 * 1000;
export var QUIET_POLL_MS = 90 * 1000;
// Past this, the board says on screen that it may be out of date.
export var STALE_AFTER_MS = 3 * 60 * 1000;

/* Whether to read everything again now.
 *
 *   reason        'rejoin' | 'visible' | 'online' | 'tick'
 *   now           ms
 *   lastCatchUpAt ms of the last read this station started on its own or
 *                 loaded in full (0 for never)
 *   lastHeardAt   ms of the last read that came back (0 or null for never)
 */
export function shouldCatchUp(o) {
  var now = o.now;
  var sinceCatchUp = now - (o.lastCatchUpAt || 0);
  if (sinceCatchUp < MIN_GAP_MS) return false;
  if (o.reason === 'tick') {
    var heard = o.lastHeardAt || 0;
    return now - heard >= QUIET_POLL_MS;
  }
  return o.reason === 'rejoin' || o.reason === 'visible' || o.reason === 'online';
}

/* The sentence for a board that has not heard from the server in a while, or
 * null when it has. `lastHeardAt` null means it has never loaded — that is the
 * load error's job to say, not this one's. */
export function staleNote(now, lastHeardAt) {
  if (!lastHeardAt) return null;
  var ms = now - lastHeardAt;
  if (ms < STALE_AFTER_MS) return null;
  var min = Math.floor(ms / 60000);
  return 'This screen last heard from the server ' + min + ' min ago, so the board and the book ' +
    'may be out of date. It is trying again — reload the page if this stays up.';
}
